import { Layers, Wifi, WifiOff, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useState } from 'react'
import { useMcpStats, useIntelligence } from '@/hooks/useApi'
import { formatGbp } from '@/lib/utils'

const CONNECTIONS = [
  { name: 'ops-bridge MCP', url: 'ops-bridge.ops-a58.workers.dev/mcp', status: 'connected' },
  { name: 'CORA Intelligence', url: 'access.corainfinitysuite.com', status: 'connected' },
  { name: 'Cloudflare MCP', url: 'cloudflare MCP server', status: 'connected' },
]

export function MCPPanel() {
  const [toolFilter, setToolFilter] = useState('')
  const { data: mcpData, isLoading: mcpLoading } = useMcpStats()
  const { data: intelData, isLoading: intelLoading } = useIntelligence()

  const toolCount = mcpData?.tool_count || 0
  const topTools = mcpData?.top_tools || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            MCP Connections
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {CONNECTIONS.map((conn) => (
              <div key={conn.name} className="flex items-center justify-between p-3 rounded-lg glass-card">
                <div className="flex items-center gap-3">
                  {conn.status === 'connected' ? (
                    <Wifi className="h-4 w-4 text-success" />
                  ) : (
                    <WifiOff className="h-4 w-4 text-danger" />
                  )}
                  <div>
                    <span className="text-sm font-medium text-text-primary">{conn.name}</span>
                    <span className="text-[11px] text-text-muted block font-mono">{conn.url}</span>
                  </div>
                </div>
                <Badge variant={conn.status === 'connected' ? 'success' : 'danger'}>{conn.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              MCP Tool Usage
              {mcpLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="muted">{toolCount} tools</Badge>
              <Input
                placeholder="Filter tools..."
                className="w-48 h-7 text-xs"
                value={toolFilter}
                onChange={(e) => setToolFilter(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {topTools
              .filter((t) => !toolFilter || t.name.toLowerCase().includes(toolFilter.toLowerCase()))
              .map((tool) => (
                <div key={tool.name} className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-elevated/50">
                  <span className="text-xs font-mono text-text-primary">{tool.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-surface-elevated rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full"
                        style={{ width: `${Math.min(100, (tool.count / (topTools[0]?.count || 1)) * 100)}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-text-muted w-8 text-right">{tool.count}</span>
                  </div>
                </div>
              ))}
            {topTools.length === 0 && !mcpLoading && (
              <div className="text-center py-4 text-sm text-text-muted">No tool usage data</div>
            )}
          </div>
        </CardContent>
      </Card>

      {intelData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              AI Providers
              {intelLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(intelData.providers).map(([name, info]) => (
                <div key={name} className="p-3 rounded-lg glass-card space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary capitalize">{name}</span>
                      {info.model && (
                        <span className="text-[11px] text-text-muted font-mono bg-surface-elevated px-1.5 py-0.5 rounded">
                          {info.model}
                        </span>
                      )}
                    </div>
                    <Badge variant={info.available ? 'success' : 'muted'}>
                      {info.available ? 'available' : 'not configured'}
                    </Badge>
                  </div>
                  {info.available && (
                    <div className="flex items-center gap-3 text-[11px] font-mono text-text-muted">
                      <span>
                        Today{' '}
                        <span className="text-text-primary">{formatGbp(info.cost_usd_today ?? 0)}</span>
                        <span className="ml-1">· {info.calls_today ?? 0} calls</span>
                      </span>
                      <span className="text-text-muted/40">|</span>
                      <span>
                        All time{' '}
                        <span className="text-text-primary">{formatGbp(info.cost_usd_total ?? 0)}</span>
                        <span className="ml-1">· {info.calls_total ?? 0} calls</span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
